import { type UserStatus } from 'next-auth'
import { v4 as generateUUID } from 'uuid'

import { CookieRegistry } from '@/constants/cookieRegistry'
import { isUserAuthenticated } from '@/domain/user'
import { type AttributeFilter, type FilterGroup, type MinMax, type RangeFilter } from '@/generated'
import {
  type FilterName,
  type ForSaleBy,
  type GptValue,
  isForSaleBy,
} from '@/lib/ads/types/adConfig'
import { createCookie } from '@/utils/cookies/createCookie'
import { getCookieByKey } from '@/utils/cookies/getCookieByKey'
import { isServer } from '@/utils/isSSR'

/**
 * This will set the `machId` once on application load on CLIENT, the first
 * call to `getMachIdFromCookie` will check for the existence and value and
 * if not present set the value to the generated UUID.
 *
 * This will also ALWAYS return a UUID instead of an empty string that will
 * persist throughout the duration of the applications lifecycle.
 */
let machIdUuid = ''

if (!isServer()) {
  machIdUuid = generateUUID()
}

export const getMachIdFromCookie = (): GptValue => {
  if (isServer()) return []

  const machIdCookie = getCookieByKey(document.cookie, CookieRegistry.MACHINE_ID)

  if (!machIdCookie) {
    createCookie(CookieRegistry.MACHINE_ID, machIdUuid)
  }

  return machIdCookie || machIdUuid
}

export const priceLabel = (priceMinMax: MinMax | undefined) => {
  if (!priceMinMax) {
    return null
  }

  const { min, max } = priceMinMax

  if (!min && !max) {
    return null
  }

  if (min && !max) {
    return `${min}_or_more`
  }

  if (!min && max) {
    return `${max}_or_less`
  }

  return `${min}_to_${max}`
}

export const yearLabel = (yearFilters: MinMax | undefined) => {
  if (!yearFilters) {
    return null
  }

  const { min, max } = yearFilters

  if (!min && !max) {
    return null
  }

  if (min && !max) {
    return `${min}_or_later`
  }

  if (!min && max) {
    return `${max}_or_earlier`
  }

  return `${min}_to_${max}`
}

type GPTForSaleBy = 'owner' | 'dealer'
export const GPTForSaleByLabel: { [x in ForSaleBy]: GPTForSaleBy } = {
  ownr: 'owner',
  delr: 'dealer',
} as const

export const forSaleByLabel = (selectedValues: string[]): GPTForSaleBy | GPTForSaleBy[] => {
  const selectedValue = selectedValues[0]

  if (selectedValues.length === 1 && isForSaleBy(selectedValue)) {
    return GPTForSaleByLabel[selectedValue]
  }

  return [GPTForSaleByLabel.ownr, GPTForSaleByLabel.delr]
}

type FilterType = AttributeFilter | RangeFilter

export const getFilterSelectedValues = (filters: FilterGroup[], name: FilterName) => {
  const filter = filters.find((filter) => filter.name === name)?.filters[0] as FilterType

  if (filter?.__typename === 'AttributeFilter') {
    return filter.selectedValues
  }

  if (filter?.__typename === 'RangeFilter') {
    return filter.selectedRangeValues
  }

  return []
}

export const singleValueArrayToString = (array: string[]): string | string[] =>
  array.length < 2 ? array.toString() : array

export const getPrebidVersion = () => {
  if (isServer()) return undefined

  const prebidVersion = window.pbjs?.version

  // remove the 'v' from the version string
  return prebidVersion ? prebidVersion.slice(1) : undefined
}

export const getIsAuthenticatedForGpt = (authenticationStatus: UserStatus) =>
  isUserAuthenticated(authenticationStatus) ? 'yes' : 'no'
